/** Suggested calibration fixes from Ace stability variance — copy as a Cursor prompt. */

import { useCallback, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { Button } from "@/components/ui/button/Button";
import type { AceStabilityTestResult } from "@/services/api/agentReview";
import {
  buildStabilityCalibrationSuggestions,
  compileStabilityCalibrationPrompt,
  phaseLabel,
} from "./aceStabilityCalibrationFixes";
import { isPartialStabilityRun, partialStabilityRunMessage } from "./aceStabilityResultHelpers";

interface AceStabilityCalibrationFixesPanelProps {
  result: AceStabilityTestResult;
  catalogueKey?: string | null;
}

export function AceStabilityCalibrationFixesPanel({
  result,
  catalogueKey,
}: AceStabilityCalibrationFixesPanelProps) {
  const [copied, setCopied] = useState(false);
  const partialRun = isPartialStabilityRun(result);
  const prompt = partialRun ? "" : compileStabilityCalibrationPrompt(result, catalogueKey);
  const suggestions = partialRun ? [] : buildStabilityCalibrationSuggestions(result, catalogueKey);

  const handleCopy = useCallback(async () => {
    if (!prompt) return;
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [prompt]);

  if (partialRun) {
    return (
      <div
        className="rounded-xl bg-white/[0.02] px-4 py-3 text-xs text-slate-400 ring-1 ring-amber-500/20"
        data-testid="ace-stability-calibration-fixes"
      >
        {partialStabilityRunMessage(result)} Re-run the stability test before calibrating checks.
      </div>
    );
  }

  if (!prompt) return null;

  return (
    <div
      className="space-y-3 rounded-xl bg-white/[0.02] px-4 py-3 ring-1 ring-amber-500/20"
      data-testid="ace-stability-calibration-fixes"
    >
      <div>
        <p className="text-[11px] font-medium uppercase tracking-wide text-amber-300/80">
          Suggested calibration fixes
        </p>
        <p className="mt-1 text-xs text-slate-400">
          Copy this prompt into Cursor to tighten the review checks that varied across runs.
        </p>
      </div>

      <ul className="space-y-2">
        {suggestions.map((item) => (
          <li
            key={`${item.kind}:${item.phase}:${item.checkId ?? item.title}`}
            className="rounded-lg bg-slate-900/40 px-3 py-2 text-xs ring-1 ring-white/5"
          >
            <p className="font-medium text-slate-200">
              [{phaseLabel(item.phase)}] {item.title}
            </p>
            <p className="mt-1 text-slate-500">{item.observation}</p>
            <p className="mt-1 text-slate-400 leading-relaxed">{item.suggestion}</p>
          </li>
        ))}
      </ul>

      <textarea
        readOnly
        value={prompt}
        rows={Math.min(14, Math.max(6, prompt.split("\n").length + 1))}
        className="w-full resize-y rounded-lg border-0 bg-slate-900/60 px-3 py-2 text-xs leading-relaxed text-slate-200 ring-1 ring-white/10"
        data-testid="ace-stability-calibration-prompt"
      />

      <div className="flex flex-wrap gap-2">
        <Button plain onClick={() => void handleCopy()} className="!text-slate-300 hover:!text-white">
          <FontAwesomeIcon icon={faCopy} />
          {copied ? "Copied" : "Copy Cursor prompt"}
        </Button>
      </div>
    </div>
  );
}
